// Whitelisted footnote-body parser + renderers.
//
// `FootnoteRefNode.attrs.body` (and the endnote equivalent) is a `string`. To
// preserve marks pasted from Word / Google Docs / LibreOffice without a schema
// change, the string may carry a constrained HTML subset:
//
//   <strong>, <em>, <a href="..." rel="noopener">, plus escaped text
//
// Anything outside the whitelist is flattened to text. Plain-text bodies (no
// `<` characters) round-trip unchanged — back-compat with all existing data.
//
// Helpers:
//   parseFootnoteBody(body)      → BodyToken[]   (tokenized representation)
//   renderFootnoteBodyHtml(body) → string        (HTML for published output)
//   renderFootnoteBodyMd(body)   → string        (Markdown for published output)
//   renderFootnoteBodyReact(body)→ ReactNode     (React for in-app previews)
//   serializeBodyFromHtml(html)  → string        (DOM → whitelisted HTML string)
//
// `serializeBodyFromHtml` is shared by the paste extractor (Word HTML) and the
// contenteditable Popover (user-edited DOM) so both go through the same
// whitelist gate.

import type { ReactNode } from "react";
import { createElement, Fragment } from "react";
import { safeUrl } from "./render/inline";

// ---------------------------------------------------------------------------
// Token model
// ---------------------------------------------------------------------------

export type BodyToken =
  | { type: "text"; text: string }
  | { type: "bold"; children: BodyToken[] }
  | { type: "italic"; children: BodyToken[] }
  | { type: "link"; href: string; children: BodyToken[] };

const escHtml = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const escMd = (s: string): string => s.replace(/([\\`*_{}[\]()#+\-.!>|])/g, "\\$1");

// ---------------------------------------------------------------------------
// DOM walker (browser + jsdom). Used by `parseFootnoteBody` to interpret the
// body string as a DOM fragment.
// ---------------------------------------------------------------------------

const walkNode = (node: Node): BodyToken[] => {
  if (node.nodeType === 3 /* text */) {
    const text = node.textContent ?? "";
    return text ? [{ type: "text", text }] : [];
  }
  if (node.nodeType !== 1) return [];
  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  const children: BodyToken[] = [];
  el.childNodes.forEach((c) => children.push(...walkNode(c)));

  if (tag === "strong" || tag === "b") return [{ type: "bold", children }];
  if (tag === "em" || tag === "i") return [{ type: "italic", children }];
  if (tag === "a") {
    const raw = el.getAttribute("href") ?? "";
    return [{ type: "link", href: safeUrl(raw), children }];
  }
  if (tag === "br") return [{ type: "text", text: " " }];
  // Anything else (span, div, p, …) flattens to its children.
  return children;
};

export const parseFootnoteBody = (body: string): BodyToken[] => {
  const raw = (body ?? "").trim();
  if (!raw) return [];
  // Fast path: no markup → plain text token.
  if (!raw.includes("<") && !raw.includes("&")) return [{ type: "text", text: raw }];
  if (typeof DOMParser === "undefined") {
    // SSR / Deno fallback: strip everything to text. The frontend always has
    // DOMParser; this branch only protects the test runner edge case.
    return [{ type: "text", text: raw.replace(/<[^>]+>/g, "") }];
  }
  const doc = new DOMParser().parseFromString(`<div>${raw}</div>`, "text/html");
  const root = doc.body.firstChild;
  if (!root) return [];
  const out: BodyToken[] = [];
  root.childNodes.forEach((c) => out.push(...walkNode(c)));
  return out;
};

// ---------------------------------------------------------------------------
// Renderers
// ---------------------------------------------------------------------------

const tokensToHtml = (tokens: BodyToken[]): string =>
  tokens
    .map((t) => {
      if (t.type === "text") return escHtml(t.text);
      const inner = tokensToHtml(t.children);
      if (t.type === "bold") return `<strong>${inner}</strong>`;
      if (t.type === "italic") return `<em>${inner}</em>`;
      return `<a href="${escHtml(t.href)}" rel="noopener">${inner}</a>`;
    })
    .join("");

const tokensToMd = (tokens: BodyToken[]): string =>
  tokens
    .map((t) => {
      if (t.type === "text") return escMd(t.text);
      const inner = tokensToMd(t.children);
      if (t.type === "bold") return `**${inner}**`;
      if (t.type === "italic") return `*${inner}*`;
      return `[${inner}](${t.href})`;
    })
    .join("");

const tokensToReact = (tokens: BodyToken[], keyPrefix = "n"): ReactNode =>
  createElement(
    Fragment,
    null,
    ...tokens.map((t, i) => {
      const key = `${keyPrefix}-${i}`;
      if (t.type === "text") return t.text;
      const inner = tokensToReact(t.children, key);
      if (t.type === "bold") return createElement("strong", { key }, inner);
      if (t.type === "italic") return createElement("em", { key }, inner);
      return createElement(
        "a",
        { key, href: t.href, rel: "noopener noreferrer", target: "_blank" },
        inner,
      );
    }),
  );

export const renderFootnoteBodyHtml = (body: string): string =>
  tokensToHtml(parseFootnoteBody(body));

export const renderFootnoteBodyMd = (body: string): string =>
  tokensToMd(parseFootnoteBody(body));

export const renderFootnoteBodyReact = (body: string): ReactNode =>
  tokensToReact(parseFootnoteBody(body));

// ---------------------------------------------------------------------------
// DOM serializer — converts an arbitrary element's children into the
// whitelisted body string. Shared by the Word-paste extractor and the
// contenteditable Popover.
// ---------------------------------------------------------------------------

const serializeNode = (node: Node): string => {
  if (node.nodeType === 3) return escHtml(node.textContent ?? "");
  if (node.nodeType !== 1) return "";
  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  const inner = Array.from(el.childNodes).map(serializeNode).join("");
  if (tag === "strong" || tag === "b") return inner ? `<strong>${inner}</strong>` : "";
  if (tag === "em" || tag === "i") return inner ? `<em>${inner}</em>` : "";
  if (tag === "a") {
    const href = safeUrl(el.getAttribute("href") ?? "");
    return inner ? `<a href="${escHtml(href)}" rel="noopener">${inner}</a>` : "";
  }
  if (tag === "br" || tag === "p" || tag === "div") {
    // Treat block boundaries as a space so adjacent words don't smash together.
    return inner ? `${inner} ` : "";
  }
  // span / sup / sub / etc. — flatten to inner.
  return inner;
};

/** Serialize an element's child DOM into a whitelisted body string. */
export const serializeBodyFromElement = (el: Element): string => {
  const out = Array.from(el.childNodes).map(serializeNode).join("");
  // Collapse runs of whitespace introduced by block flattening.
  return out.replace(/\s+/g, " ").trim();
};

/** Serialize a raw HTML string the same way (paste path). */
export const serializeBodyFromHtml = (html: string): string => {
  if (!html) return "";
  if (typeof DOMParser === "undefined") {
    return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstChild as Element | null;
  if (!root) return "";
  return serializeBodyFromElement(root);
};

/** Plain-text projection of a body string — used by the legacy Textarea path
 *  and by aria labels. */
export const footnoteBodyPlainText = (body: string): string => {
  const tokens = parseFootnoteBody(body);
  const flatten = (ts: BodyToken[]): string =>
    ts.map((t) => (t.type === "text" ? t.text : flatten(t.children))).join("");
  return flatten(tokens).replace(/\s+/g, " ").trim();
};
